const { getElements } = window.BAO.Utils

/**
 * @typedef {Object} SelectedVariantOption
 *
 * @property {string} label
 * @property {string} value
 */

const selectors = {
  addToCart: '[data-product-add-to-cart]',
  addToCartText: '[data-product-add-to-cart-text]',
  backInStockTrigger: '[data-product-bis-trigger]',
  form: '[data-product-form]',
  price: '[data-product-price]',
  sku: '[data-product-sku]',
  variantIndicator: '[data-product-variant-indicator]',
  variantContent: '[data-product-variant-content]',
  variantDrawerTrigger: '[data-product-variant-trigger]',
}

class Variant {
  constructor (data) {
    /**
     * @type {ShopifyVariant}
     * @public
     */
    this.data = data
  }

  async setPrice (context) {
    await window.BAO.sectionRenderer
      .fetch(
        'product-price',
        `${context.dataset.url}?variant=${this.data.id}&section_id=product-price`,
      )
      .then((res) => {
        const newDOM = window.BAO.sectionRenderer.convertToDOM(
          res['product-price'],
        )
        const myself = newDOM.querySelector('.shopify-section')

        if (!myself) return

        const priceElements = getElements(
          `${selectors.price}[data-form-id='${context.dataset.formId}']`,
        )

        priceElements.forEach((el) => {
          el.innerHTML = myself.innerHTML
        })
      })
  }

  setAvailability (context) {
    const endOfLife = context.dataset.endOfLife === 'true'
    const showBackInStock = !this.data.available && !endOfLife

    getElements(
      `${selectors.addToCart}[data-form-id='${context.dataset.formId}']`,
    ).forEach((el) => {
      el.disabled = !this.data.available
      el.setAttribute('aria-hidden', showBackInStock)
    })

    getElements(
      `${selectors.backInStockTrigger}[data-form-id='${context.dataset.formId}']`,
    ).forEach((el) => {
      el.setAttribute('aria-hidden', !showBackInStock)
      el.setAttribute('data-product-url', this.url.toString())
    })

    let addToCartText = window.theme.strings.addToCart

    const preorderData = JSON.parse(
      document
        .querySelector('product-preorder')
        ?.getAttribute('data-preorder-data') || '[]',
    )

    const variantIdString = this.data.id.toString()
    const variantData = preorderData.find(
      (item) => item.id === variantIdString,
    )

    const productTag = document.querySelector('.prd-Tags_Tag-preorder')
    const formFooter = document.querySelector('.prd-Form_Footer')
    const formFooterContainer = document.querySelector(
      '.prd-Form_FooterAncillaries',
    )
    const formFooterButtonEl = document.querySelector('.prd-Form_Button')
    const formFooterPreorderButtonIcon = document.querySelector(
      '.prd-Form_ButtonIcon-preorder',
    )
    const formFooterButtonIcon = document.querySelector(
      '.prd-Form_ButtonIcon-regular',
    )

    const formFixedBarAncillaries = document.querySelector(
      '.prd-FixedBar_Ancillaries',
    )
    const formFixedBarButtonEl = document.querySelector('.prd-FixedBar_Button')
    const formFixedBarPreorderButtonIcon = document.querySelector(
      '.prd-FixedBar_ButtonIcon-preorder',
    )
    const formFixedBarButtonIcon = document.querySelector(
      '.prd-FixedBar_ButtonIcon-regular',
    )

    if (variantData?.enable === 'true') {
      addToCartText = window.theme.strings.preOrder

      productTag?.setAttribute('aria-hidden', false)
      formFooter?.classList.add('prd-Form_Footer-preorder')
      formFooterButtonEl?.classList.add('prd-Form_Button-preorder')
      formFooterContainer?.setAttribute('aria-hidden', true)
      formFooterPreorderButtonIcon?.setAttribute('aria-hidden', false)
      formFooterButtonIcon?.setAttribute('aria-hidden', true)

      formFixedBarAncillaries?.setAttribute('aria-hidden', true)
      formFixedBarButtonEl?.classList.add('prd-FixedBar_Button-preorder')
      formFixedBarPreorderButtonIcon?.setAttribute('aria-hidden', false)
      formFixedBarButtonIcon?.setAttribute('aria-hidden', true)
    } else {
      productTag?.setAttribute('aria-hidden', true)
      formFooter?.classList.remove('prd-Form_Footer-preorder')
      formFooterButtonEl?.classList.remove('prd-Form_Button-preorder')
      formFooterContainer?.setAttribute('aria-hidden', false)
      formFooterPreorderButtonIcon?.setAttribute('aria-hidden', true)
      formFooterButtonIcon?.setAttribute('aria-hidden', false)

      formFixedBarAncillaries?.setAttribute('aria-hidden', false)
      formFixedBarButtonEl?.classList.remove('prd-FixedBar_Button-preorder')
      formFixedBarPreorderButtonIcon?.setAttribute('aria-hidden', true)
      formFixedBarButtonIcon?.setAttribute('aria-hidden', false)
    }

    getElements(
      `${selectors.addToCartText}[data-form-id='${context.dataset.formId}']`,
    ).forEach((el) => {
      el.textContent = this.data.available
        ? addToCartText
        : window.theme.strings.soldOut
    })
  }

  updateInput (context) {
    const inputs = getElements(
      `form[action*="/cart/add"][id="${context.dataset.formId}"] [name="id"]`,
    )

    inputs.forEach((input) => {
      input.value = this.id
      input.dispatchEvent(new Event('change', { bubbles: true }))
    })
  }

  updateIndicator (name, value, context) {
    const variantIndicators = getElements(
      `${selectors.variantIndicator}[data-form-id='${context.dataset.formId}']`,
    )

    const variantIndicator = variantIndicators.find(
      (el) => el.dataset.name === name,
    )

    if (!variantIndicator) return

    variantIndicator.textContent = value
  }

  updatePackageContent (name, value, context) {
    const variantContents = getElements(
      `${selectors.variantContent}[data-form-id='${context.dataset.formId}']`,
    )

    const variantContent = variantContents.find(
      (el) => el.dataset.name === name,
    )

    if (!variantContent) return

    variantContent.textContent = value || ''
  }

  setUrl () {
    window.history.replaceState(
      { path: this.url.toString() },
      '',
      this.url.toString(),
    )
  }

  setShareUrl () {
    const shareButtons = getElements('share-button')

    if (shareButtons.length === 0) return

    shareButtons.forEach((shareButton) => {
      shareButton.updateUrl(this.url.toString())
    })
  }

  /**
   * @returns {number}
   */
  get id () {
    return this.data.id
  }

  /**
   * @returns {URL}
   */
  get url () {
    // eslint-disable-next-line compat/compat
    const url = new URL(window.location.href)

    url.searchParams.set('variant', this.id.toString())

    return url
  }
}

export class VariantOptions extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['data', 'option', 'option-choice']
  }

  constructor () {
    super()

    /**
     * @type {Variant[]}
     * @public
     */
    this.collectedVariants = []
  }

  setupListeners () {
    super.setupListeners()

    this.onVariantChange = this.onVariantChange.bind(this)

    this.listeners.add(this, 'change', this.onVariantChange)

    this.updateOptionStates()
  }

  onVariantChange (e) {
    if (!e.target.matches(this.els['option-choice'].selector)) return

    const changedOptionIndex = this.els.option.elements.findIndex((optionEl) =>
      optionEl.contains(e.target),
    )

    this.ensureValidSelections(changedOptionIndex)
    this.updateOptionStates()

    const variant = this.getVariant()

    if (!variant) {
      this.setUnavailableCombination()

      return
    }

    variant.setAvailability(this)
    variant.setPrice(this)
    variant.updateInput(this)

    this.updateSelectedOptionContent(variant)

    window.BAO.dispatchEvent(window.BAO.EVENTS.PRODUCT.VARIANT_CHANGE, {
      variant,
    })

    variant.setUrl()
    variant.setShareUrl()

    const drawer = e.target.closest('site-drawer')
    const drawerButton = drawer?.querySelector('[data-drawers-close]')

    drawerButton?.click()
  }

  ensureValidSelections (changedOptionIndex) {
    for (
      let optionIndex = changedOptionIndex + 1;
      optionIndex < this.els.option.elements.length;
      optionIndex++
    ) {
      const optionEl = this.els.option.elements[optionIndex]
      const choices = this.getChoices(optionEl)

      const selectedChoice = choices.find((choice) => choice.checked)

      if (
        selectedChoice &&
        this.getMatchingVariants(optionIndex, selectedChoice.value).length > 0
      ) {
        continue
      }

      const availableChoice = choices.find((choice) =>
        this.getMatchingVariants(optionIndex, choice.value).some(
          (variant) => variant.available,
        ),
      )

      const existingChoice =
        availableChoice ||
        choices.find(
          (choice) =>
            this.getMatchingVariants(optionIndex, choice.value).length > 0,
        )

      if (existingChoice) {
        existingChoice.checked = true
      }
    }
  }

  updateOptionStates () {
    this.els.option.elements.forEach((optionEl, optionIndex) => {
      this.getChoices(optionEl).forEach((choice) => {
        const matchingVariants = this.getMatchingVariants(
          optionIndex,
          choice.value,
        )

        const exists = matchingVariants.length > 0
        const available = matchingVariants.some((variant) => variant.available)

        choice.disabled = !exists
        choice.dataset.variantExists = exists.toString()
        choice.dataset.variantAvailable = available.toString()

        const item = choice.closest('.drw-Variants_Item')
        const label = item?.querySelector(selectors.variantDrawerTrigger)

        if (item) {
          item.hidden = !exists
        }

        if (!label) return

        label.dataset.variantExists = exists.toString()
        label.dataset.variantAvailable = available.toString()
        label.setAttribute('aria-disabled', (!exists).toString())
      })
    })
  }

  updateSelectedOptionContent (variant) {
    this.els.option.elements.forEach((optionEl) => {
      const selectedChoice = this.getChoices(optionEl).find(
        (choice) => choice.checked,
      )

      if (!selectedChoice) return

      variant.updateIndicator(
        optionEl.dataset.name,
        selectedChoice.dataset.value || selectedChoice.value,
        this,
      )

      variant.updatePackageContent(
        optionEl.dataset.name,
        selectedChoice.dataset.packageContent,
        this,
      )
    })
  }

  getMatchingVariants (optionIndex, value) {
    const selectedOptions = this.selectedOptions

    return this.data.filter((variant) => {
      if (variant.options[optionIndex] !== value) return false

      for (let index = 0; index < optionIndex; index++) {
        if (variant.options[index] !== selectedOptions[index]?.value) {
          return false
        }
      }

      return true
    })
  }

  getChoices (optionEl) {
    return getElements(this.els['option-choice'].selector, {
      context: optionEl,
    })
  }

  setUnavailableCombination () {
    getElements(
      `${selectors.addToCart}[data-form-id='${this.dataset.formId}']`,
    ).forEach((el) => {
      el.disabled = true
      el.setAttribute('aria-hidden', false)
    })

    getElements(
      `${selectors.backInStockTrigger}[data-form-id='${this.dataset.formId}']`,
    ).forEach((el) => {
      el.setAttribute('aria-hidden', true)
    })

    getElements(
      `${selectors.addToCartText}[data-form-id='${this.dataset.formId}']`,
    ).forEach((el) => {
      el.textContent = window.theme.strings.soldOut
    })
  }

  /**
   * @param {ShopifyVariant} data
   *
   * @returns {Variant}
   */
  createVariant (data) {
    const variant = new Variant(data)

    this.collectedVariants.push(variant)

    return variant
  }

  /**
   * @returns {Variant|null}
   */
  getVariant () {
    if (!this.currentVariant) return null

    const existingVariant = this.collectedVariants.find(
      (variant) => variant.id === this.currentVariant.id,
    )

    if (existingVariant) return existingVariant

    return this.createVariant(this.currentVariant)
  }

  /**
   * @returns {SelectedVariantOption[]}
   */
  get selectedOptions () {
    this.throwError('selectedOptions needs to be implemented on this class')

    return []
  }

  /**
   * @returns {ShopifyVariant|undefined}
   */
  get currentVariant () {
    const selectedOptions = this.selectedOptions

    return this.data.find((variant) => {
      return variant.options.every(
        (option, index) => selectedOptions[index]?.value === option,
      )
    })
  }

  /**
   * @returns {ShopifyVariant[]}
   */
  get data () {
    return JSON.parse(this.els.data.element.innerText)
  }
}

export class VariantRadios extends VariantOptions {
  get selectedOptions () {
    return this.els.option.elements.map((el) => {
      const { name } = el.dataset

      const choice = getElements(this.els['option-choice'].selector, {
        context: el,
      }).find((choiceEl) => choiceEl.checked)

      return {
        label: name,
        value: choice?.value,
      }
    })
  }
}

export class VariantSelects extends VariantOptions {
  get selectedOptions () {
    return this.els.option.elements.map((el) => {
      const { name } = el.dataset

      return {
        label: name,
        value: el.querySelector('select')?.value,
      }
    })
  }
}

if (!customElements.get('variant-radios')) {
  customElements.define('variant-radios', VariantRadios)
}

if (!customElements.get('variant-selects')) {
  customElements.define('variant-selects', VariantSelects)
}
